/**
 * Affichage d'un fuseau horaire — l'heure qu'il est LÀ-BAS, et l'écart avec ici.
 *
 * Retour PO (2026-08-03) : la fiche intervenant affichait « Indian/Reunion ».
 * C'est un identifiant technique, et il ne répond pas à la question que se pose
 * réellement la personne qui regarde cet écran — *« puis-je l'appeler
 * maintenant ? »*. On remplace donc l'identifiant par ce qui décide :
 *   • l'heure QU'IL EST chez l'intervenant, en direct ;
 *   • l'écart avec l'heure de celui qui consulte (« 2 h d'avance sur vous »).
 *
 * La référence est le fuseau du NAVIGATEUR : c'est littéralement l'heure locale
 * de l'appelant, sans donnée à charger et sans hypothèse sur l'endroit d'où il
 * travaille (un même organisme peut avoir des salariés à Paris et à Saint-Denis).
 *
 * Tolérance aux fuseaux d'une demi-heure ou d'un quart d'heure (Inde +5 h 30,
 * Népal +5 h 45, Chatham +12 h 45) : l'écart est calculé en MINUTES.
 */
/** Instant `date` exprimé dans `timeZone`, sous forme de Date « murale ». */
function wallClock(date: Date, timeZone: string): Date {
    return new Date(date.toLocaleString('en-US', { timeZone }))
}

/**
 * Fuseau du navigateur — l'heure locale de la personne qui consulte.
 *
 * ⚠️ **Elle CONSTRUIT un `Intl.DateTimeFormat` à chaque appel** pour ne lire
 * qu'un réglage déjà connu du moteur, et elle est le DÉFAUT d'`offsetMinutes`,
 * `sameWallClock`, `timeZoneHint` et `instantDansLeFuseau` : dans un corps de
 * composant, elle coûte par RENDU. ⛔ **Mais elle ne se met PAS en cache ici** —
 * essayé, mesuré, retiré le 2026-09-19 : un cache de module fige la première
 * réponse pour la vie du processus, et `loc019-lheure-dun-lieu-est-celle-du-site`
 * pose `process.env.TZ` EN COURS DE PASSE (Node le relit à chaud) pour juger la
 * mention du fuseau — le cache l'a fait rougir sur du code juste. La parade est
 * chez l'APPELANT, déjà écrite quatre fois : `useMemo(() => viewerTimeZone(), [])`
 * — une résolution par MONTAGE, pas par rendu.
 */
export function viewerTimeZone(): string {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone || 'Europe/Paris'
    } catch {
        return 'Europe/Paris'
    }
}

/**
 * Écart en MINUTES entre `timeZone` et le fuseau de référence, à l'instant `now`.
 * Positif = là-bas il est plus tard. `null` si un des fuseaux est inconnu.
 *
 * Calculé sur l'instant courant, donc l'heure d'été est prise en compte
 * automatiquement — un écart figé en dur serait faux la moitié de l'année.
 */
export function offsetMinutes(
    timeZone: string,
    reference: string = viewerTimeZone(),
    now: Date = new Date(),
): number | null {
    try {
        const diffMs = wallClock(now, timeZone).getTime() - wallClock(now, reference).getTime()
        return Math.round(diffMs / 60_000)
    } catch {
        return null
    }
}

/** Heure locale « 21:14 » dans `timeZone`. `null` si le fuseau est inconnu. */
export function localTime(
    timeZone: string,
    locale: string = 'fr-FR',
    now: Date = new Date(),
): string | null {
    try {
        return new Intl.DateTimeFormat(locale, {
            timeZone,
            hour: '2-digit',
            minute: '2-digit',
        }).format(now)
    } catch {
        return null
    }
}

/**
 * Nom lisible du fuseau (« heure de La Réunion ») plutôt que « Indian/Reunion ».
 * Repli sur l'identifiant brut si l'environnement ne sait pas le nommer.
 *
 * Cache des noms de fuseau — même moule que `getDateFormatter` (`date-utils`).
 *
 * ⚠️ Posé le 2026-09-18, au contrôle : depuis que l'infobulle du calendrier
 * nomme le fuseau, cette fonction est appelée **par cellule occupée**, et elle
 * construisait un `Intl.DateTimeFormat` NEUF à chaque fois (0,074 ms contre
 * 0,00024 ms au cache, mesuré — facteur ~300). Sur une vue de 28 jours, la
 * promesse écrite en tête de `heuresDeLaLigne` — « aucun `Intl` neuf par
 * cellule » — était devenue fausse.
 *
 * 🔑 **La clé porte le JOUR**, jamais l'instant : le nom ne change qu'avec la
 * saison (« heure d'été » / « heure normale »), et une clé à la milliseconde ne
 * mutualiserait rien. Une vue mensuelle borne donc le cache à quelques dizaines
 * d'entrées par fuseau.
 */
const nomsDeFuseau = new Map<string, string>()

export function timeZoneLabel(
    timeZone: string,
    locale: string = 'fr-FR',
    now: Date = new Date(),
): string {
    // ⚠️ Un INDEX de jour, jamais `toISOString().slice(0,10)` : le gardien des
    // dates interdit cette écriture (un jour tiré d'un ISO est le jour UTC, et
    // il part décalé d'un jour dans tout fuseau à décalage positif). Ici seul
    // le REGROUPEMENT compte — le nom d'un fuseau ne change qu'avec la saison —
    // et un entier le fait aussi bien, sans chaîne ni `Intl`.
    const cle = `${locale}|${timeZone}|${Math.floor(now.getTime() / 86_400_000)}`
    const connu = nomsDeFuseau.get(cle)
    if (connu !== undefined) return connu
    try {
        const parts = new Intl.DateTimeFormat(locale, {
            timeZone,
            timeZoneName: 'long',
        }).formatToParts(now)
        const nom = parts.find((p) => p.type === 'timeZoneName')?.value || timeZone
        nomsDeFuseau.set(cle, nom)
        return nom
    } catch {
        // Un fuseau que le moteur ne connaît pas : on mémorise aussi ce repli,
        // sinon chaque cellule d'une grille entière paierait la levée.
        nomsDeFuseau.set(cle, timeZone)
        return timeZone
    }
}

/**
 * Le fuseau d'un site donne-t-il la MÊME heure que celui du lecteur ?
 *
 * C'est la question — et la seule — qui décide si une heure de lieu doit être
 * dite à l'heure du site et son fuseau mentionné (arbitrage PO du 2026-09-18).
 * Écrite ICI une fois, pour les trois surfaces qui la posent : le volet de
 * synthèse, l'aperçu ⌘K, la bulle de la carte — et l'infobulle du calendrier,
 * qui la posait avant les autres.
 *
 * ⚠️ **On compare des DÉCALAGES, jamais des identifiants.** `Europe/Brussels` et
 * `Europe/Paris` sont deux chaînes différentes et la même heure : mentionner un
 * fuseau qui ne change rien à ce qui est affiché apprend seulement à ne plus lire
 * la mention. Et le décalage est calculé à l'instant courant, donc l'heure d'été
 * suit.
 *
 * Un fuseau vide ou que le moteur ne connaît pas rend `true` : pas de mention,
 * pas de conversion — l'heure du lecteur, c'est-à-dire ce qui se passait avant.
 */
export function sameWallClock(
    timeZone: string | null | undefined,
    reference: string = viewerTimeZone(),
    now: Date = new Date(),
): boolean {
    if (!timeZone) return true
    const offset = offsetMinutes(timeZone, reference, now)
    return offset === null || offset === 0
}

/** « 2 h », « 5 h 30 », « 45 min » — valeur absolue, sans signe. */
export function formatOffsetDuration(minutes: number): string {
    const total = Math.abs(minutes)
    const hours = Math.floor(total / 60)
    const rest = total % 60
    if (hours === 0) return `${rest} min`
    return rest === 0 ? `${hours} h` : `${hours} h ${String(rest).padStart(2, '0')}`
}

export interface TimeZoneHint {
    /** Heure qu'il est sur place, ou `null` si le fuseau est illisible. */
    time: string | null
    /** Nom lisible du fuseau. */
    label: string
    /** Écart en minutes avec le fuseau de référence (`null` si indéterminable). */
    offset: number | null
    /** Vrai s'il y a un décalage réel — c'est ce qui justifie la mise en exergue. */
    differs: boolean
}

export function timeZoneHint(
    timeZone: string | null | undefined,
    locale: string = 'fr-FR',
    now: Date = new Date(),
    reference: string = viewerTimeZone(),
): TimeZoneHint | null {
    if (!timeZone) return null
    const offset = offsetMinutes(timeZone, reference, now)
    return {
        time: localTime(timeZone, locale, now),
        label: timeZoneLabel(timeZone, locale, now),
        offset,
        differs: offset !== null && offset !== 0,
    }
}

/**
 * Le fuseau qu'un écran a le droit d'ANNONCER, à partir du couple servi par le
 * serveur — et lui seul.
 *
 * 🔴 **Les deux champs ne répondent PAS à la même question**, et c'est le piège
 * exact que l'arbitrage PO du 2026-09-18 nomme. `site_timezone` est le fuseau
 * RÉSOLU : la cascade `COALESCE(lieu, organisme)` l'a déjà comblé, il n'est
 * donc jamais nul et répondrait TOUJOURS « oui, il y a un fuseau ». C'est
 * `site_timezone_declared` — et lui seul — qui dit si un LIEU en déclare un.
 * Trancher sur le premier ferait écrire « heure de Paris » sur un matériel dont
 * personne ne sait où il est.
 *
 * `null` en sortie veut dire « aucun fuseau de site » : l'appelant garde alors
 * l'heure du LECTEUR, et le DIT. ⛔ « Fuseau non renseigné » n'est pas
 * « site parisien ».
 *
 * ⚠️ Les deux arguments sont `unknown` à dessein : la même règle sert des
 * charges TYPÉES (une réservation) et un `context: Record<string, unknown>` de
 * constat de conflit. Une seconde écriture pour le second, c'est la moitié
 * qu'on oublierait de corriger.
 */
export function fuseauDeclare(declare: unknown, resolu: unknown): string | null {
    return declare === true && typeof resolu === 'string' && resolu ? resolu : null
}

/**
 * Un instant, écrit à l'heure DE LÀ-BAS quand cela change quelque chose.
 *
 * Déplacée ici depuis `summary/primitives.helpers.ts` le 2026-09-19 : la même
 * phrase est désormais attendue sur le volet de synthèse, sur la bulle de la
 * carte ET sur les constats de conflit d'une réservation de matériel. Trois
 * écritures de « faut-il mentionner le fuseau ? », ce sont trois réponses le
 * jour où la règle bouge.
 *
 * Retour PO du 2026-09-18 : un lieu à La Réunion annonçait « occupé jusqu'à
 * 11 h 30 » à un lecteur parisien pour une occupation qui se termine à 15 h 30
 * SUR PLACE. L'heure était juste — c'est le même instant — et illisible.
 *
 * Le fuseau est MENTIONNÉ dès qu'il change l'heure affichée, et jamais sinon
 * (`sameWallClock`) : mentionner un fuseau qui ne change rien apprend seulement
 * à ne plus lire la mention. Le nom vient d'`Intl` dans la langue de l'écran
 * (« heure de La Réunion »), jamais l'identifiant technique.
 *
 * ⚠️ `dateStyle`/`timeStyle` ne se combinent PAS avec `timeZoneName` — `Intl`
 * lève. D'où la mention accolée par le texte, et non demandée au formateur.
 *
 * ⚠️ **Les deux questions se posent à l'instant AFFICHÉ (`quand`), jamais à
 * l'instant COURANT** — d'où le troisième argument passé aux deux appels.
 * Défaut mesuré le 2026-09-18, sur un écran lu en JANVIER : pour une occupation
 * qui se termine le 15 juillet 2027 à 13:00 UTC dans un lieu à `Africa/Tunis`
 * (UTC+1 toute l'année), `sameWallClock(tz)` comparait les décalages du jour de
 * LECTURE — en janvier Paris est aussi à UTC+1, donc « même heure », donc aucune
 * conversion : l'écran affichait 15:00 (l'heure de Paris en juillet) sans la
 * moindre mention, là où il est 14:00 sur place. Et le NOM souffrait de la même
 * faute : une réservation de janvier lue en juillet se nommait « heure d'été
 * d'Europe centrale », et la même ligne changeait de nom six mois plus tard
 * sans que la donnée bouge.
 *
 * Cache de formateurs de DATE — jumeau de `nomsDeFuseau` ci-dessus. Construire
 * un `Intl.DateTimeFormat` coûte ~300 fois le formatage : une liste de constats
 * ou un volet de synthèse en construisait un par ligne.
 *
 * 🔴 **Et NON, il n'emprunte pas celui de `date-utils`** — c'est mesuré, pas
 * esthétique. `date-utils` importe `@/i18n`, qui appelle `initReactI18next` au
 * CHARGEMENT : lui emprunter son cache a fait tomber `localTimeHint.test.tsx`
 * dès la collecte (« No "initReactI18next" export is defined on the
 * "react-i18next" mock »), sur un fichier qui n'importe que celui-ci. Ce module
 * est une FEUILLE, sans une seule dépendance, et c'est cette propriété qui le
 * rend montable partout. Six lignes de mémo sont le prix de cette propriété.
 */
const formateursDeDate = new Map<string, Intl.DateTimeFormat>()

function formateurDeDate(locale: string, options: Intl.DateTimeFormatOptions) {
    const cle = `${locale}|${JSON.stringify(options)}`
    const connu = formateursDeDate.get(cle)
    if (connu) return connu
    const neuf = new Intl.DateTimeFormat(locale, options)
    formateursDeDate.set(cle, neuf)
    return neuf
}

export function instantDansLeFuseau(
    quand: Date,
    locale: string,
    fuseau?: string | null,
    options: { avecHeure?: boolean } = { avecHeure: true },
    /** ⚠️ Le fuseau du LECTEUR, **donnée d'entrée** (2026-09-19) — même moule
     *  que le `lecteur` d'`heuresDeLaLigne`. Il décide des deux moitiés : faut-il
     *  convertir, et dans quel repère écrire sinon. Défaut = l'ambiance. */
    lecteur: string = viewerTimeZone(),
): string {
    const style = options.avecHeure === false
        ? ({ dateStyle: 'medium' } as const)
        : ({ dateStyle: 'medium', timeStyle: 'short' } as const)
    const ici = () => formateurDeDate(locale, { ...style, timeZone: lecteur }).format(quand)
    if (!fuseau || sameWallClock(fuseau, lecteur, quand)) return ici()
    try {
        return `${formateurDeDate(locale, { ...style, timeZone: fuseau }).format(quand)}`
            + ` (${timeZoneLabel(fuseau, locale, quand)})`
    } catch {
        // Fuseau que le moteur ne sait pas lire : l'heure du lecteur plutôt
        // qu'une ligne qui lève et emporte la section entière.
        return ici()
    }
}
